import { createClient } from 'graphql-ws'
import { useEffect, useMemo, useRef } from 'react'
import Observable from 'zen-observable'

import { useUserContext } from '@/context'

const WS_URL = process.env.WEBSOCKET_URL || ''

export function useSubscriptions(operation) {
  const { user } = useUserContext()

  // Keep a ref so connectionParams always reads the latest token without
  // needing to recreate the client on every auth_token refresh.
  const userRef = useRef(user)
  userRef.current = user

  // Memoize the client so it is only recreated when the user identity changes
  // (logout / re-login), not on every render.
  const subscriptionClient = useMemo(
    () =>
      createClient({
        url: WS_URL,
        // Function form so the token is read at connection time, not captured
        // as a stale value when the client object was constructed.
        connectionParams: () => ({
          authToken: userRef.current?.auth_token,
        }),
        keepAlive: 30000,
        retryAttempts: 5,
        shouldRetry: () => true,
        retryWait: async function randomisedExponentialBackoff(retries: number) {
          let retryDelay = 1000
          for (let i = 0; i < retries; i++) {
            retryDelay *= 2
          }
          await new Promise((resolve) => setTimeout(resolve, retryDelay + Math.random() * 1000))
        },
        lazy: true,
        lazyCloseTimeout: 300000,
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [user?.id],
  )

  useEffect(() => {
    return () => {
      subscriptionClient.dispose()
    }
  }, [subscriptionClient])

  return new Observable((observer) =>
    subscriptionClient.subscribe(operation, {
      next: (data) => observer.next(data),
      error: (error: any) => {
        // eslint-disable-next-line no-console
        console.error('[WebSocket] Subscription error:', error)
        observer.error(error)
      },
      complete: () => observer.complete(),
    }),
  )
}
